import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AgentCoreService } from '../services/agentcore';
import './ChatInterface.css';

interface ChatInterfaceProps {
  token: string;
}

interface Message {
  type: 'user' | 'assistant';
  content: string;
  thinking?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ token }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [thinking, setThinking] = useState('');
  const [thinkingHistory, setThinkingHistory] = useState(''); // Backup thinking content
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const apiService = new AgentCoreService(token);

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    console.log('🚀 Starting sendMessage with input:', input);
    
    const userMessage: Message = { type: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setThinking(''); // Clear thinking when starting new message
    setThinkingHistory(''); // Clear thinking history
    setIsStreaming(true);

    console.log('📤 Calling apiService.sendMessage...');

    try {
      const stream = await apiService.sendMessage(input);
      console.log('✅ Received stream from API:', stream);
      
      const reader = stream.getReader();
      console.log('📖 Got stream reader:', reader);
      
      const decoder = new TextDecoder();
      console.log('🔤 Created TextDecoder');

      let assistantContent = '';
      let accumulatedThinking = ''; // Local accumulator for thinking content
      let chunkCount = 0;

      try {
        console.log('🔄 Starting stream reading loop...');
        
        let sseBuffer = ''; // Buffer for incomplete SSE messages across chunks
        
        while (true) {
          console.log(`📥 Reading chunk ${chunkCount + 1}...`);
          const { done, value } = await reader.read();
          
          console.log(`📦 Chunk ${chunkCount + 1} - done:`, done, 'value length:', value?.length);
          
          if (done) {
            console.log('✅ Stream reading completed');
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          const timestamp = new Date().toISOString();
          console.log(`🔍 [${timestamp}] Decoded chunk ${chunkCount + 1}:`, chunk);
          
          // Add chunk to buffer
          sseBuffer += chunk;
          
          // Split buffer into lines
          const lines = sseBuffer.split('\n');
          
          // Keep the last line in buffer (might be incomplete)
          sseBuffer = lines.pop() || '';
          
          console.log(`📝 [${timestamp}] Processing ${lines.length} complete lines, buffer length: ${sseBuffer.length}, buffer: "${sseBuffer}"`);

          for (const line of lines) {
            const lineTimestamp = new Date().toISOString();
            console.log(`📄 [${lineTimestamp}] Processing line (length: ${line.length}):`, line);
            
            if (line.startsWith('data: ')) {
              let dataContent = line.slice(6);
              console.log(`🎯 [${lineTimestamp}] Found SSE data (typeof: ${typeof dataContent}, length: ${dataContent.length}):`, dataContent);
              
              try {
                // Parse once
                let data = JSON.parse(dataContent);
                console.log(`✅ [${lineTimestamp}] First parse - typeof:`, typeof data);
                
                // If result is a string, parse again (double-encoded JSON)
                if (typeof data === 'string') {
                  console.log(`🔄 [${lineTimestamp}] Data is string, parsing again...`);
                  data = JSON.parse(data);
                  console.log(`✅ [${lineTimestamp}] Second parse - typeof:`, typeof data);
                }
                
                console.log(`✅ [${lineTimestamp}] Final parsed data:`, data);
                const messageType = String(data.type || '').trim();
                console.log(`🔍 [${lineTimestamp}] Message type: "${messageType}"`);
                
                if (messageType === 'thinking') {
                  console.log(`🤔 [${lineTimestamp}] AI thinking:`, data.content);
                  accumulatedThinking += `${data.content}\n\n`; // Keep local copy
                  setThinking(prev => prev + `${data.content}\n\n`);
                  setThinkingHistory(prev => prev + `${data.content}\n\n`); // Also save to history
                  setTimeout(scrollToBottom, 100); // Scroll after content is rendered
                } else if (messageType === 'agent_handoff') {
                  console.log(`🤝 [${lineTimestamp}] Agent handoff:`, data.content);
                  accumulatedThinking += `${data.content}\n\n`;
                  setThinking(prev => prev + `${data.content}\n\n`);
                  setThinkingHistory(prev => prev + `${data.content}\n\n`);
                  setTimeout(scrollToBottom, 100);
                } else if (messageType === 'tool_call') {
                  // Ignore tool_call messages - they're internal implementation details
                  console.log(`🔧 [${lineTimestamp}] Tool call (hidden from UI):`, data.content);
                } else if (messageType === 'final_answer') {
                  const answer = data.data?.answer || data.data || data.content;
                  console.log(`🎉 [${lineTimestamp}] Final answer received:`, answer);
                  
                  // Backend now sends clean report_writer content
                  assistantContent = typeof answer === 'string' ? answer : JSON.stringify(answer, null, 2);
                  console.log(`📝 [${lineTimestamp}] Extracted content length:`, assistantContent.length);
                } else if (messageType === 'error') {
                } else if (messageType === 'debug') {
                  console.log(`🐛 [${lineTimestamp}] Debug message:`, data.message);
                } else if (messageType === 'complete') {
                  console.log(`✅ [${lineTimestamp}] Stream completed:`, data);
                } else {
                  console.log(`❓ [${lineTimestamp}] Unknown message type: "${messageType}" (raw: ${data.type})`, data);
                }
              } catch (parseError) {
                console.warn(`⚠️ [${lineTimestamp}] Failed to parse SSE data:`, dataContent, 'Error:', parseError);
              }
            } else if (line.trim()) {
              console.log(`📄 [${lineTimestamp}] Non-SSE line (length: ${line.length}):`, line);
            } else {
              console.log(`📄 [${lineTimestamp}] Empty line`);
            }
          }
          
          chunkCount++;
        }
      } finally {
        console.log('🔓 Releasing stream reader lock');
        reader.releaseLock();
      }

      console.log('🏁 Stream processing complete. Assistant content:', assistantContent);
      console.log('🤔 Final thinking:', thinking);
      console.log('🤔 Accumulated thinking:', accumulatedThinking);
      console.log('🤔 Final thinking history:', thinkingHistory);
      console.log('🤔 Thinking length:', thinking.length);
      console.log('🤔 Accumulated thinking length:', accumulatedThinking.length);
      console.log('🤔 Thinking history length:', thinkingHistory.length);

      // Use thinking state as the primary source since it's been accumulating during streaming
      const finalThinking = thinking || thinkingHistory || accumulatedThinking;
      console.log('🤔 Using final thinking:', finalThinking);
      console.log('🤔 Final thinking length:', finalThinking.length);
      
      setIsStreaming(false);
      const assistantMessage: Message = {
        type: 'assistant',
        content: assistantContent || 'No response received from the agent.',
        thinking: finalThinking
      };
      
      console.log('💬 Adding assistant message:', assistantMessage);
      console.log('💬 Message thinking content:', assistantMessage.thinking);
      setMessages(prev => [...prev, assistantMessage]);
      
      // Don't clear thinking - keep it visible after final answer
      // DON'T clear thinking here - let it persist so user can see it

    } catch (error) {
      console.error('💥 Error in sendMessage:', error);
      console.error('💥 Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      
      setIsStreaming(false);
      const errorMessage: Message = {
        type: 'assistant',
        content: `Sorry, I encountered an error processing your request: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
      
      console.log('❌ Adding error message:', errorMessage);
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      console.log('🔚 sendMessage finally block - setting loading to false');
      setLoading(false);
    }
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <img src="/meppy-avatar.png" alt="Meppy" className="chat-header-avatar" />
        <div className="chat-header-content">
          <h2>Ask about Methane Pyrolysis</h2>
          <p>Get expert analysis from our multi-agent research system</p>
          <span style={{
            fontSize: '0.75rem',
            color: '#666',
            marginTop: '0.25rem',
            display: 'block'
          }}>
            Backend: {(window as any).MEPPY_BACKEND || 'meppy-agentcore'}
          </span>
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.type}`}>
            {message.type === 'user' ? (
              <div className="message-content">
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </div>
                <button 
                  className="copy-button"
                  onClick={() => copyToClipboard(message.content)}
                  title="Copy to clipboard"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              </div>
            ) : (
              <>
                {console.log(`Message ${index} thinking:`, message.thinking, 'Length:', message.thinking?.length)}
                {(message.thinking || true) && (
                  <div className="thinking-drawer">
                    <div 
                      className="thinking-drawer-header" 
                      onClick={() => {
                        const content = document.getElementById(`thinking-${index}`);
                        const icon = document.getElementById(`thinking-icon-${index}`);
                        if (content && icon) {
                          const isExpanded = content.style.display !== 'none';
                          content.style.display = isExpanded ? 'none' : 'block';
                          icon.textContent = isExpanded ? '▶' : '▼';
                        }
                      }}
                    >
                      <span className="thinking-drawer-title">💭 AI Thinking Process</span>
                      <span id={`thinking-icon-${index}`} className="thinking-drawer-icon">▶</span>
                    </div>
                    <div id={`thinking-${index}`} className="thinking-drawer-content" style={{display: 'none'}}>
                      <pre className="thinking-formatted">{message.thinking || 'No thinking content'}</pre>
                    </div>
                  </div>
                )}
                <div className="message-content">
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  </div>
                  <button 
                    className="copy-button"
                    onClick={() => copyToClipboard(message.content)}
                    title="Copy to clipboard"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {isStreaming && thinking && (
          <div className="message assistant">
            {thinking && (
              <div className="thinking-content">
                <div className="thinking-header">💭 AI Thinking (Live)</div>
                <div className="thinking-text">
                  <pre className="thinking-formatted">{thinking}</pre>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Ask about methane pyrolysis..."
          disabled={loading}
        />
        <button onClick={sendMessage} disabled={loading || !input.trim()}>
          {loading ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default ChatInterface;
